import { useEffect, useState } from 'react';

import { homeworkPrintBackLink } from '@/pages/teacher/homework/homeworkLinks';

type UserNavigation =
  typeof import('@/components/admin/userManagement/userNavigation');
let cached: UserNavigation | undefined;
let loading: Promise<UserNavigation> | undefined;

const HOMEWORK_PRINT_PATH = /^\/teacher\/homework\/([^/]+)\/print$/;

/**
 * The homework Save as PDF preview opens from the class page and from the
 * homework page. The section it returns to stays lit, decided by the same
 * validated origin as its back link.
 */
function homeworkPrintSection(
  pathname: string,
  search: string
): string | undefined {
  const match = HOMEWORK_PRINT_PATH.exec(pathname);
  if (!match) return undefined;
  const back = homeworkPrintBackLink(match[1], new URLSearchParams(search));
  return back.to.split('?')[0];
}

/** Only shared user records need the origin validators (and their schemas). */
export function useNavSection(pathname = '', search = ''): string | undefined {
  const isUserRecord = /^\/admin\/users\/[^/]+$/.test(pathname);
  const [navigation, setNavigation] = useState(cached);
  const [error, setError] = useState<unknown>();
  useEffect(() => {
    if (!isUserRecord || navigation) return;
    let active = true;
    loading ??= import('@/components/admin/userManagement/userNavigation').then(
      module => {
        cached = module;
        return module;
      },
      failure => {
        loading = undefined;
        throw failure;
      }
    );
    void loading.then(
      module => {
        if (active) setNavigation(module);
      },
      failure => {
        if (active) setError(failure);
      }
    );
    return () => {
      active = false;
    };
  }, [isUserRecord, navigation]);
  if (!isUserRecord) return homeworkPrintSection(pathname, search);
  if (error) throw error;
  // Keep the highlight unset until its validated origin is known. The rest of
  // navigation remains usable, and ordinary routes never request this module.
  return navigation ? navigation.navSectionForLocation(pathname, search) : '';
}
